import React from 'react';

import { BrandColor } from '@styleseat/brand';
import { Inline } from '@styleseat/ui';
import {
  BasicHomeIcon,
  BasicSearchIcon,
  BasicUserIcon,
  CircleQuestionIcon,
  GiftBoxIcon,
  ReactionsHappyIcon,
  TimeCalendarIcon,
} from '../../ui/Icons';
import { SwapHorizontalIcon } from '../../ui/Icons/SwapHorizontalIcon';
import {
  BodyText,
  Stack,
  Separator,
} from '../../ui';
import { MOBILE_APP_START_ROUTE } from '../../../route-names';
import TrackedOption from './TrackedOption';
import { ShareProps, ShareTrigger } from '../../consumer/sharing';

interface SidebarClientSectionProps {
  isLoggedIn: boolean;
  isProvider: boolean;
  utmParams: {};
  referralEntrypointProps: ShareProps;
  goToSearch: () => void;
  linkTo: (url: string, params: {}) => void;
  onLogin: () => void;
  switchToBusiness: () => void;
  onSignUp: () => void;
  goToHome: () => void;
  goToMyAccount: () => void;
  goToMyAppointments: () => void;
}

const SidebarClientSection: React.FC<SidebarClientSectionProps> = ({
  isLoggedIn,
  isProvider,
  utmParams,
  referralEntrypointProps,
  goToSearch,
  linkTo,
  onLogin,
  switchToBusiness,
  onSignUp,
  goToHome,
  goToMyAccount,
  goToMyAppointments,
}) => (
  <Stack padding={20} space={16}>
    {!isLoggedIn && (
      <BodyText appearance="larger" weight="bold">
        For Clients
      </BodyText>
    )}
    <Separator color={BrandColor.LightGrey} appearance="extrathin" paddingTop={20} />
    {!isLoggedIn && (
      <TrackedOption
        name="sidebar-sign-up-to-book-clicked"
        testID="sidebar-sign-up-to-book-option"
        onPress={onSignUp}
      >
        <Inline space={16}>
          <ReactionsHappyIcon width={20} height={20} />
          <BodyText weight="bold">
            Sign up to book
          </BodyText>
        </Inline>
        <Separator color={BrandColor.LightGrey} appearance="extrathin" paddingTop={20} />
      </TrackedOption>
    )}
    {isProvider && (
      <TrackedOption
        name="sidebar-switch-to-business-clicked"
        testID="sidebar-switch-to-business-option"
        onPress={switchToBusiness}
      >
        <Inline space={16}>
          <SwapHorizontalIcon width={20} height={20} />
          <BodyText>
            Switch to business
          </BodyText>
        </Inline>
        <Separator color={BrandColor.LightGrey} appearance="extrathin" paddingTop={20} />
      </TrackedOption>
    )}
    <TrackedOption
      name="sidebar-home-clicked"
      testID="sidebar-home-option"
      onPress={goToHome}
    >
      <Inline space={16}>
        <BasicHomeIcon width={20} height={20} />
        <BodyText>
          Home
        </BodyText>
      </Inline>
      <Separator color={BrandColor.LightGrey} appearance="extrathin" paddingTop={20} />
    </TrackedOption>
    <TrackedOption
      name="sidebar-search-clicked"
      testID="sidebar-search-option"
      onPress={goToSearch}
    >
      <Inline space={16}>
        <BasicSearchIcon width={20} height={20} />
        <BodyText>
          Search
        </BodyText>
      </Inline>
      <Separator color={BrandColor.LightGrey} appearance="extrathin" paddingTop={20} />
    </TrackedOption>
    {!isLoggedIn && (
      <TrackedOption
        name="sidebar-how-to-book-appt-clicked"
        testID="sidebar-how-to-book-appt-option"
        onPress={
          () => linkTo(MOBILE_APP_START_ROUTE, {
            signupWallScreen: 'splash',
            ...utmParams,
          })
        }
      >
        <Inline space={16}>
          <CircleQuestionIcon width={20} height={20} />
          <BodyText>
            How to book an appointment
          </BodyText>
        </Inline>
        <Separator color={BrandColor.LightGrey} appearance="extrathin" paddingTop={20} />
      </TrackedOption>
    )}
    {isLoggedIn && (
      <TrackedOption
        name="sidebar-my-appointments-clicked"
        testID="sidebar-my-appointments-option"
        onPress={goToMyAppointments}
      >
        <Inline space={16}>
          <TimeCalendarIcon width={20} height={20} />
          <BodyText>
            My Appointments
          </BodyText>
        </Inline>
        <Separator color={BrandColor.LightGrey} appearance="extrathin" paddingTop={20} />
      </TrackedOption>
    )}
    {isLoggedIn && (
      <TrackedOption
        name="sidebar-my-account-clicked"
        testID="sidebar-my-account-option"
        onPress={goToMyAccount}
      >
        <Inline space={16}>
          <BasicUserIcon width={20} height={20} />
          <BodyText>
            My Account
          </BodyText>
        </Inline>
        <Separator color={BrandColor.LightGrey} appearance="extrathin" paddingTop={20} />
      </TrackedOption>
    )}
    <ShareTrigger
      {...referralEntrypointProps}
      renderTrigger={onClick => (
        <TrackedOption
          name="sidebar-get-50-clicked"
          testID="sidebar-get-50-option"
          onPress={isLoggedIn ? onClick : onLogin}
        >
          <Inline space={16}>
            <GiftBoxIcon width={20} height={20} />
            <BodyText>
              Get $50
            </BodyText>
          </Inline>
          <Separator color={BrandColor.LightGrey} appearance="extrathin" paddingTop={20} />
        </TrackedOption>
      )}
    />
  </Stack>
);

export default SidebarClientSection;
